// src/features/tasks/pages/CreateTaskPage.jsx
import React, { useState, useCallback, useEffect } from 'react';
import {
  Alert,
} from '@mui/material';
import {
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { addDays, subDays, addHours, startOfDay, format, isSameDay } from 'date-fns';

// Components
import TaskForm from '../components/TaskForm';

// Hooks
import { useTasks } from '../hooks/useTasks';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useTranslation } from 'react-i18next';
import { FormPage, FormActions } from '../../../components/common/form';

export default function CreateTaskPage() {
  const navigate = useNavigate();
  const { navigateBack } = useNavigationStack();
  const location = useLocation();
  const { t } = useTranslation(["common", "tasks"]);
  const { showSuccess, showError } = useSnackbar();

  // Check if we have template data from location state
  const templateTask = location.state?.templateTask;
  // Set by flows like onboarding that want the user sent back after creating
  const returnTo = location.state?.returnTo;
  // Equipment to pre-select, e.g. when creating a task for a specific item from onboarding
  const prefillEquipment = location.state?.prefillEquipment;

  // State
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category_id: '',
    procedure_id: null,
    template_id: null,
    template_version: null,
    priority: 'MEDIUM',
    scheduled_date: new Date(),
    due_date: addHours(new Date(), 48), // Default to 48 hours from now
    becomes_public_at: startOfDay(new Date()),
    assignment_mode: 'simple',
    primary_assignee_id: null,
    secondary_assignee_id: null,
    weekday_assignments: null,
    is_recurring: false,
    rrule: null,
    skip_closing_days: false,
    requires_dual_signature: false,
    mark_not_completed_when_overdue: false,
    quick_complete: true,
    equipment: [],
    manual_file_ids: [],
    send_email: false,
    email_recipient: '',
    email_subject: '',
    email_body: '',
  });
  const [validationErrors, setValidationErrors] = useState({});

  // API hooks
  const {
    createTask,
    loading,
    error,
    clearError,
  } = useTasks();

  // Load template data if available
  useEffect(() => {
    if (templateTask) {
      setFormData(prev => ({
        ...prev,
        title: templateTask.name || '',
        description: templateTask.description || '',
        category_id: templateTask.category_id || '',
        is_recurring: templateTask.is_recurring || false,
        rrule: templateTask.recurrence_rule || null,
        template_id: templateTask.template_id || templateTask.id || null,
        template_version: templateTask.version || null,
        skip_closing_days: templateTask.skip_closing_days || false,
        requires_dual_signature: templateTask.requires_dual_signature || false,
        mark_not_completed_when_overdue: templateTask.mark_not_completed_when_overdue || false,
        quick_complete: templateTask.quick_complete ?? true,
        manual_file_ids: templateTask.manual_id ? [templateTask.manual_id] : [],
        send_email: templateTask.send_email || false,
        email_recipient: templateTask.email_recipient || '',
        email_subject: templateTask.email_subject || '',
        email_body: templateTask.email_body || '',
      }));
    }
  }, [templateTask]);

  useEffect(() => {
    if (!prefillEquipment?.length) return;
    // Same entry shape as TaskForm's handleEquipmentAdd
    setFormData(prev => ({
      ...prev,
      equipment: prefillEquipment.map(item => ({
        ...item,
        equipment_id: item.equipment_id,
        relation_type: 'MAINTENANCE',
        notes: null,
        rotation_label: null,
        rotation_options: null,
      })),
    }));
  }, [prefillEquipment]);

  // Form handling
  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear validation error for this field
    setValidationErrors(prev => {
      if (prev[field]) {
        const { [field]: removed, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  const validateForm = useCallback(() => {
    const errors = {};

    // Required fields
    if (!formData.title?.trim()) {
      errors.title = t('tasks:validation.title_required');
    } else if (formData.title.length > 255) {
      errors.title = t('tasks:validation.title_too_long');
    }

    if (!formData.category_id) {
      errors.category_id = t('tasks:validation.category_required');
    }

    if (!formData.scheduled_date) {
      errors.scheduled_date = t('tasks:validation.start_required');
    }

    if (!formData.due_date) {
      errors.due_date = t('tasks:validation.deadline_required');
    }

    if (!formData.becomes_public_at) {
      errors.becomes_public_at = t('tasks:validation.public_required');
    }

    // Date validations
    if (formData.scheduled_date && formData.due_date) {
      if (formData.due_date < formData.scheduled_date) {
        errors.due_date = t('tasks:validation.deadline_before_start');
      }
    }

    if (formData.becomes_public_at && formData.scheduled_date) {
      if (formData.becomes_public_at > formData.scheduled_date) {
        errors.becomes_public_at = t('tasks:validation.public_after_start');
      }
    }

    // Description length
    if (formData.description && formData.description.length > 2000) {
      errors.description = t('tasks:validation.description_too_long');
    }

    // Assignee validation
    if (formData.primary_assignee_id === formData.secondary_assignee_id && formData.primary_assignee_id) {
      errors.secondary_assignee_id = t('tasks:validation.secondary_same_as_primary');
    }

    // Recurring task validation
    if (formData.is_recurring) {
      if (!formData.rrule) {
        errors.rrule = t('tasks:validation.recurrence_required');
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, t]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Format equipment for API - only include essential fields
      const equipmentList = (formData.equipment || []).map((eq) => ({
        equipment_id: eq.equipment_id,
        relation_type: eq.relation_type,
        notes: eq.notes || null,
        rotation_label: eq.rotation_label || null,
        rotation_options: eq.rotation_options?.length ? eq.rotation_options : null,
      }));

      // Format dates for API
      const taskData = {
        ...formData,
        scheduled_date: formData.scheduled_date.toISOString().split('T')[0],
        due_date: formData.due_date.toISOString().split('T')[0],
        becomes_public_at: startOfDay(formData.becomes_public_at).toISOString().split('T')[0],
        assignment_mode: formData.assignment_mode || 'simple',
        primary_assignee_id: formData.assignment_mode === 'simple' ? formData.primary_assignee_id : null,
        secondary_assignee_id: formData.assignment_mode === 'simple' ? formData.secondary_assignee_id : null,
        weekday_assignments: formData.assignment_mode === 'weekday' ? formData.weekday_assignments : null,
        rrule: formData.is_recurring ? formData.rrule : null,
        skip_closing_days: formData.skip_closing_days || false,
        requires_dual_signature: formData.requires_dual_signature || false,
        mark_not_completed_when_overdue: formData.mark_not_completed_when_overdue || false,
        quick_complete: formData.quick_complete ?? true,
        template_id: formData.template_id || null,
        equipment: equipmentList,
        manual_file_ids: formData.manual_file_ids || [],
      };

      const result = await createTask(taskData);

      if (result.success) {
        showSuccess(t('tasks:success.task_created'));
        const taskId = result.data?.data?.task_id;
        if (returnTo) {
          navigate(returnTo);
        } else if (taskId) {
          navigate(`/tasks/${taskId}`);
        } else {
          navigate('/tasks');
        }
      } else {
        showError(result.error || t('common:messages.error_occurred'));
      }
    } catch (error) {
      showError(t('common:messages.error_occurred'));
    }
  }, [validateForm, formData, createTask, showSuccess, showError, navigate, returnTo, t]);

  const handleCancel = useCallback(() => {
    if (returnTo) {
      navigate(returnTo);
    } else {
      navigateBack('/tasks');
    }
  }, [navigate, navigateBack, returnTo]);

  return (
    <FormPage
      title={t("tasks:pages.create_task")}
      subtitle={t("tasks:pages.create_subtitle")}
      onBack={handleCancel}
      onSubmit={handleSubmit}
      fieldErrors={validationErrors}
      error={error}
      onErrorClose={clearError}
      maxWidth={960}
    >
      {templateTask && (
        <Alert severity="info">
          {t("tasks:catalog.template_loaded", { name: templateTask.name })}
        </Alert>
      )}
      <TaskForm
        formData={formData}
        onInputChange={handleInputChange}
        validationErrors={validationErrors}
        loading={loading}
        mode="create"
      />
      <FormActions
        onCancel={handleCancel}
        submitType="submit"
        submitLabel={loading ? t("tasks:pages.creating") : t("tasks:pages.create_task")}
        submitIcon={<SaveIcon />}
        loading={loading}
      />
    </FormPage>
  );
}
